(() => {
  const ROOT_SELECTOR = '[data-ezgf]';
  const DATA_VIEW = 'ez-gf-data';
  const collectionCache = new Map();

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  }[ch]));

  const track = (root, name, props = {}) => {
    if (root.dataset.track !== 'true') return;
    try {
      if (Array.isArray(window._learnq)) window._learnq.push(['track', name, props]);
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...props });
    } catch (_) {}
  };

  const collectionUrl = (handle, page = 1) => {
    const base = window.Shopify?.routes?.root || '/';
    const root = base.endsWith('/') ? base : `${base}/`;
    const params = new URLSearchParams({ view: DATA_VIEW });
    if (page > 1) params.set('page', String(page));
    return `${root}collections/${encodeURIComponent(handle)}?${params}`;
  };

  const fetchCollectionPage = async (handle, page = 1) => {
    const key = `${handle}|${page}`;
    if (collectionCache.has(key)) return collectionCache.get(key);

    const promise = fetch(collectionUrl(handle, page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('[data-ezgf-collection-json]');
        if (!script) throw new Error(`Gift Finder data missing for ${handle}`);
        return JSON.parse(script.textContent);
      });

    collectionCache.set(key, promise);
    return promise;
  };

  const fetchCollection = async (handle, { maxPages = 4 } = {}) => {
    if (!handle) return { handle: '', title: '', url: '', products: [], pages: 1 };
    const first = await fetchCollectionPage(handle, 1);
    const pages = Math.min(Number(first.pages || 1), maxPages);
    if (pages <= 1) return first;

    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, i) => fetchCollectionPage(handle, i + 2))
    );

    const seen = new Set();
    const products = [];
    [first, ...rest].forEach((set) => (set.products || []).forEach((product) => {
      const id = String(product.id);
      if (!seen.has(id)) {
        seen.add(id);
        products.push(product);
      }
    }));

    return { ...first, pages, products };
  };

  const productCard = (product) => `
    <li class="ezgf-product">
      <article class="ezgf-product__card">
        <a class="ezgf-product__media" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="700" height="700">` : ''}
        </a>
        <div class="ezgf-product__content">
          <a class="ezgf-product__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <div class="ezgf-product__rating" aria-label="Product reviews">
            ${product.rating_html || `<div class="jdgm-widget jdgm-preview-badge" data-id="${escapeHtml(product.id)}" data-auto-install="false"></div>`}
          </div>
          <div class="ezgf-product__price-row">
            <strong>${escapeHtml(product.price)}</strong>
            ${product.compare_at ? `<s>${escapeHtml(product.compare_at)}</s>` : ''}
          </div>
        </div>
      </article>
    </li>`;

  const refreshJudgeMe = () => {
    window.setTimeout(() => {
      try {
        if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') window.jdgm.initializeWidgets();
      } catch (_) {}
    }, 60);
  };

  const parseSignals = (value = '') => String(value)
    .split('|')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .map((part) => {
      if (part.startsWith('contains:')) return { type: 'contains', value: part.slice(9).trim() };
      if (part.startsWith('exact:')) return { type: 'exact', value: part.slice(6).trim() };
      return { type: 'exact', value: part };
    })
    .filter((signal) => signal.value);

  const tagSet = (product) => {
    if (product.__ezgfTagSet) return product.__ezgfTagSet;
    product.__ezgfTagSet = new Set((product.tags || []).map((tag) => String(tag).trim().toLowerCase()));
    return product.__ezgfTagSet;
  };

  const matchesSignals = (product, signals = []) => {
    if (!signals.length) return false;
    const tags = tagSet(product);
    const tagList = Array.from(tags);
    return signals.some((signal) => {
      if (signal.type === 'contains') return tagList.some((tag) => tag.includes(signal.value));
      return tags.has(signal.value);
    });
  };

  const setUrlState = (state, mode = 'replace') => {
    const url = new URL(window.location.href);
    if (state.recipient?.param) url.searchParams.set('recipient', state.recipient.param);
    else url.searchParams.delete('recipient');
    if (state.occasion?.param) url.searchParams.set('occasion', state.occasion.param);
    else url.searchParams.delete('occasion');
    url.searchParams.delete('interest');
    history[mode === 'push' ? 'pushState' : 'replaceState']({ ezgf: true }, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const init = (root) => {
    if (root.dataset.ezgfReady === 'true') return;
    root.dataset.ezgfReady = 'true';

    const finder = root.querySelector('[data-ezgf-finder]');
    const recipientStep = root.querySelector('[data-ezgf-step="recipient"]');
    const occasionStep = root.querySelector('[data-ezgf-step="occasion"]');
    const summary = root.querySelector('[data-ezgf-summary]');
    const summaryChips = root.querySelector('[data-ezgf-summary-chips]');
    const results = root.querySelector('[data-ezgf-results]');
    const resultsTitle = root.querySelector('[data-ezgf-results-title]');
    const exactGrid = root.querySelector('[data-ezgf-exact-grid]');
    const loading = root.querySelector('[data-ezgf-loading]');
    const empty = root.querySelector('[data-ezgf-empty]');
    const occasionFilter = root.querySelector('[data-ezgf-occasion-filter]');
    const progressBar = root.querySelector('[data-ezgf-progress-bar]');
    const progressLabel = root.querySelector('[data-ezgf-progress-label]');
    const backButton = root.querySelector('[data-ezgf-back]');
    const resultsOccasionChips = Array.from(root.querySelectorAll('[data-ezgf-occasion-chip]'));
    const stepOccasionButtons = Array.from(root.querySelectorAll('[data-ezgf-step-occasion]'));
    const viewAllWrap = root.querySelector('[data-ezgf-results-footer]');
    const viewAll = root.querySelector('[data-ezgf-results-viewall]');
    const limit = Number(root.dataset.resultsLimit || 8);
    const minMatches = Number(root.dataset.minMatches || 2);

    const state = { recipient: null, occasion: null };
    let recipientSet = null;
    let recipientToken = 0;

    const recipientButtons = Array.from(root.querySelectorAll('[data-ezgf-option][data-kind="recipient"]'));

    const recipientData = (button) => ({
      kind: 'recipient',
      title: button.dataset.title || button.textContent.trim(),
      param: button.dataset.param || '',
      collection: button.dataset.collection || ''
    });

    const occasionData = (button) => ({
      title: button.dataset.title || button.textContent.trim(),
      param: button.dataset.param || '',
      matchMode: button.dataset.matchMode || 'tags',
      signals: parseSignals(button.dataset.matchTags || '')
    });

    const definedOccasions = () => resultsOccasionChips
      .filter((button) => (button.dataset.param || '') !== '')
      .map(occasionData);

    const specificOccasions = () => definedOccasions().filter((item) => item.matchMode !== 'everyday');

    const matchesOccasion = (product, item) => {
      if (!item || !item.param) return true;
      if (item.matchMode === 'everyday') {
        const tags = tagSet(product);
        if (tags.has('occasion:everyday')) return true;
        return !specificOccasions().some((definition) => matchesSignals(product, definition.signals));
      }
      return matchesSignals(product, item.signals);
    };

    const updateFinderStepUI = () => {
      const hasRecipient = Boolean(state.recipient);
      recipientStep.hidden = hasRecipient;
      occasionStep.hidden = !hasRecipient;
      summary.hidden = !hasRecipient;
      backButton.style.visibility = hasRecipient ? 'visible' : 'hidden';
      if (progressBar) progressBar.style.width = hasRecipient ? '100%' : '50%';
      if (progressLabel) progressLabel.textContent = hasRecipient ? '2 of 2' : '1 of 2';
    };

    const updateSummary = () => {
      if (!state.recipient) {
        summary.hidden = true;
        summaryChips.innerHTML = '';
        finder.classList.remove('is-results');
        updateFinderStepUI();
        return;
      }
      summaryChips.innerHTML = `<span>${escapeHtml(state.recipient.title)}</span>${state.occasion ? `<span>${escapeHtml(state.occasion.title)}</span>` : ''}`;
      summary.hidden = false;
      finder.classList.add('is-results');
      updateFinderStepUI();
    };

    const updateRecipientStates = () => {
      recipientButtons.forEach((button) => {
        button.classList.toggle('is-selected', Boolean(state.recipient && state.recipient.param === button.dataset.param));
      });
    };

    const setOccasionControlState = (button, active) => {
      button.classList.toggle('is-active', active);
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    };

    const syncOccasionStates = () => {
      const activeParam = state.occasion?.param || '';
      resultsOccasionChips.forEach((button) => setOccasionControlState(button, (button.dataset.param || '') === activeParam));
      stepOccasionButtons.forEach((button) => setOccasionControlState(button, (button.dataset.param || '') === activeParam));
    };

    const updateOccasionAvailability = (products = []) => {
      const availability = new Map();
      definedOccasions().forEach((item) => {
        const count = products.filter((product) => matchesOccasion(product, item)).length;
        availability.set(item.param, count);
      });

      let visible = 0;
      resultsOccasionChips.forEach((button) => {
        const param = button.dataset.param || '';
        if (!param) {
          button.hidden = false;
          button.disabled = false;
          return;
        }
        const count = availability.get(param) || 0;
        const show = count >= minMatches;
        button.hidden = !show;
        button.disabled = !show;
        button.dataset.matchCount = String(count);
        if (show) visible += 1;
      });

      stepOccasionButtons.forEach((button) => {
        const param = button.dataset.param || '';
        const count = availability.get(param) || 0;
        const show = count >= minMatches;
        button.hidden = !show;
        button.disabled = !show;
        button.dataset.matchCount = String(count);
      });

      occasionFilter.hidden = visible === 0;

      if (state.occasion) {
        const hasActive = (availability.get(state.occasion.param) || 0) >= minMatches;
        if (!hasActive) state.occasion = null;
      }
      syncOccasionStates();
    };

    const heading = () => {
      if (!state.recipient) return 'Gifts picked for you';
      if (state.occasion) return `${state.occasion.title} gifts for ${state.recipient.title}`;
      return `Gifts for ${state.recipient.title}`;
    };

    const currentProducts = () => {
      const products = recipientSet?.products || [];
      if (!state.occasion) return products;
      return products.filter((product) => matchesOccasion(product, state.occasion));
    };

    const renderCurrent = ({ historyMode = 'replace' } = {}) => {
      if (!state.recipient || !recipientSet) return;
      const products = currentProducts();
      resultsTitle.textContent = heading();
      exactGrid.innerHTML = products.slice(0, limit).map(productCard).join('');
      empty.hidden = products.length > 0;
      loading.hidden = true;

      if (viewAll && recipientSet.url) {
        viewAll.href = recipientSet.url;
        viewAll.textContent = `View all gifts for ${state.recipient.title} →`;
        viewAllWrap.hidden = false;
      } else if (viewAllWrap) {
        viewAllWrap.hidden = true;
      }

      updateSummary();
      updateRecipientStates();
      syncOccasionStates();
      setUrlState(state, historyMode);
      refreshJudgeMe();

      track(root, 'Gift Finder Results Viewed', {
        recipient: state.recipient.param,
        occasion: state.occasion?.param || 'all',
        results: products.length
      });
    };

    const openSheet = (kind) => {
      const sheet = root.querySelector(`[data-ezgf-sheet="${kind}"]`);
      if (!sheet) return;
      sheet.hidden = false;
      requestAnimationFrame(() => sheet.classList.add('is-open'));
      document.documentElement.classList.add('ezgf-lock');
    };

    const closeSheets = () => {
      root.querySelectorAll('[data-ezgf-sheet]').forEach((sheet) => {
        sheet.classList.remove('is-open');
        setTimeout(() => { sheet.hidden = true; }, 180);
      });
      document.documentElement.classList.remove('ezgf-lock');
    };

    const selectRecipient = async (button, { historyMode = 'push', requestedOccasion = '' } = {}) => {
      const item = recipientData(button);
      if (!item.collection) return;

      const current = ++recipientToken;
      state.recipient = item;
      state.occasion = null;
      recipientSet = null;
      closeSheets();
      results.hidden = false;
      loading.hidden = false;
      exactGrid.innerHTML = '';
      empty.hidden = true;
      occasionFilter.hidden = true;
      if (viewAllWrap) viewAllWrap.hidden = true;
      resultsTitle.textContent = `Gifts for ${item.title}`;
      updateSummary();
      updateRecipientStates();
      syncOccasionStates();
      setUrlState(state, historyMode);

      track(root, 'Gift Finder Recipient Selected', { recipient: item.param });

      try {
        const first = await fetchCollectionPage(item.collection, 1);
        if (current !== recipientToken || state.recipient?.param !== item.param) return;
        recipientSet = first;
        updateOccasionAvailability(first.products || []);

        if (requestedOccasion) {
          const match = definedOccasions().find((occasion) => occasion.param === requestedOccasion);
          if (match && (first.products || []).some((product) => matchesOccasion(product, match))) state.occasion = match;
        }

        renderCurrent({ historyMode: 'replace' });

        if (Number(first.pages || 1) > 1) {
          fetchCollection(item.collection, { maxPages: 4 }).then((full) => {
            if (current !== recipientToken || state.recipient?.param !== item.param) return;
            recipientSet = full;
            updateOccasionAvailability(full.products || []);
            renderCurrent({ historyMode: 'replace' });
          }).catch(() => {});
        }
      } catch (error) {
        if (current !== recipientToken) return;
        loading.hidden = true;
        empty.hidden = false;
        console.warn('[EZ Gift Finder v2.5]', error);
      }
    };

    const selectOccasion = (button, { historyMode = 'push' } = {}) => {
      if (!state.recipient || !recipientSet || button.disabled) return;
      const item = occasionData(button);
      state.occasion = item.param ? item : null;
      syncOccasionStates();
      renderCurrent({ historyMode });
      track(root, 'Gift Finder Occasion Selected', { occasion: item.param || 'all' });
    };

    const clearAll = () => {
      recipientToken += 1;
      state.recipient = null;
      state.occasion = null;
      recipientSet = null;
      results.hidden = true;
      loading.hidden = true;
      exactGrid.innerHTML = '';
      occasionFilter.hidden = true;
      if (viewAllWrap) viewAllWrap.hidden = true;
      resultsOccasionChips.forEach((chip) => {
        chip.hidden = false;
        chip.disabled = false;
      });
      stepOccasionButtons.forEach((button) => {
        button.hidden = false;
        button.disabled = false;
      });
      updateSummary();
      updateRecipientStates();
      syncOccasionStates();
      setUrlState(state, 'replace');
    };

    recipientButtons.forEach((button) => {
      button.addEventListener('click', () => selectRecipient(button));
      const prefetch = () => {
        if (button.dataset.collection) fetchCollectionPage(button.dataset.collection, 1).catch(() => {});
      };
      button.addEventListener('mouseenter', prefetch, { once: true });
      button.addEventListener('focus', prefetch, { once: true });
      button.addEventListener('pointerdown', prefetch, { once: true });
    });

    resultsOccasionChips.forEach((button) => button.addEventListener('click', () => selectOccasion(button)));
    stepOccasionButtons.forEach((button) => button.addEventListener('click', () => selectOccasion(button)));

    root.querySelectorAll('[data-ezgf-open-sheet]').forEach((button) => {
      button.addEventListener('click', () => openSheet(button.dataset.ezgfOpenSheet));
    });
    root.querySelectorAll('[data-ezgf-close-sheet]').forEach((button) => button.addEventListener('click', closeSheets));
    root.querySelectorAll('[data-ezgf-clear]').forEach((button) => button.addEventListener('click', clearAll));
    root.querySelectorAll('[data-ezgf-clear-occasion]').forEach((button) => {
      button.addEventListener('click', () => {
        state.occasion = null;
        renderCurrent({ historyMode: 'push' });
      });
    });
    if (backButton) {
      backButton.addEventListener('click', () => {
        if (!state.recipient) return;
        clearAll();
      });
    }

    const params = new URLSearchParams(window.location.search);
    const recipientParam = params.get('recipient');
    const occasionParam = params.get('occasion') || '';
    const recipientButton = recipientButtons.find((button) => button.dataset.param === recipientParam);

    if (recipientButton) selectRecipient(recipientButton, { historyMode: 'replace', requestedOccasion: occasionParam });
    else clearAll();

    const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 300));
    idle(() => {
      recipientButtons.slice(0, 6).forEach((button) => {
        if (button.dataset.collection) fetchCollectionPage(button.dataset.collection, 1).catch(() => {});
      });
    });

    window.addEventListener('popstate', () => window.location.reload());
  };

  const initDiscovery = () => {
    document.querySelectorAll('[data-ezgfd-love-root]').forEach((root) => {
      if (root.dataset.ready === 'true') return;
      root.dataset.ready = 'true';
      const cards = Array.from(root.querySelectorAll('[data-ezgfd-love-tab]'));
      const panels = Array.from(root.querySelectorAll('[data-ezgfd-love-panel]'));
      cards.forEach((card) => {
        card.addEventListener('click', () => {
          const id = card.dataset.ezgfdLoveTab;
          cards.forEach((item) => item.classList.toggle('is-active', item === card));
          panels.forEach((panel) => {
            const active = panel.dataset.ezgfdLovePanel === id;
            panel.hidden = !active;
            panel.classList.toggle('is-active', active);
          });
          refreshJudgeMe();
        });
      });
    });

    document.querySelectorAll('[data-ezgfd-trend]').forEach((root) => {
      if (root.dataset.ready === 'true') return;
      root.dataset.ready = 'true';
      root.querySelectorAll('[data-ezgfd-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const id = tab.dataset.ezgfdTab;
          root.querySelectorAll('[data-ezgfd-tab]').forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          root.querySelectorAll('[data-ezgfd-panel]').forEach((panel) => {
            const active = panel.dataset.ezgfdPanel === id;
            panel.hidden = !active;
            panel.classList.toggle('is-active', active);
          });
          refreshJudgeMe();
        });
      });
    });
  };

  const start = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
    initDiscovery();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
